import React, { useCallback, useMemo } from "react";
import clsx from "clsx";
import { useLocation, useNavigate } from "react-router-dom";
import { MoneyhopLogo } from "../../../../../assets/Icons";
import { styles } from "./styles";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { BackArrowBlack, RoundedCloseRed } from "../../../images/svg";
import Typography from "../../../../../components/ui/Typography";
import { remittanceActions } from "../../../state/remittanceSlice";
import { ConfirmCancelModal } from "../../modal";
import { AppStrings } from "../../../utils";
import { useDigioEsignEnabled } from "../../../hooks/useDigioEsignEnabled";
import { ProgressStepItem } from "../../../types/remittance/progressStep.types";

export const RemittanceProgressStepper = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isDigioEsignEnabled = useDigioEsignEnabled();

  const progresStepData = useAppSelector((state) => state.remittance.progresStep.progresStepData);
  const activeStepNumber = useAppSelector((state) => state.remittance.progresStep.activeStepNumber);
  const transactionCancelModal = useAppSelector(
    (state) => state.remittance.progresStep.transactionCancelModal
  );
  const digioNavigationGuard = useAppSelector(
    (state) => state.remittance.progresStep.digioNavigationGuard
  );
  const digioRecord = useAppSelector((state) => state.remittance.review.digioEsign.data);

  const shouldBlockNavigation = useCallback(
    (targetStepId: number) => {
      const isOnReviewScreen = activeStepNumber === 5 || location.pathname.includes("/send/review");

      return isOnReviewScreen && isDigioEsignEnabled && Boolean(digioRecord) && targetStepId < 5;
    },
    [activeStepNumber, location.pathname, digioRecord, isDigioEsignEnabled]
  );

  const navigateToStep = useCallback(
    (step: ProgressStepItem) => {
      dispatch(remittanceActions.setActiveStepNumber(step.id));
      navigate(step.pathName);
    },
    [dispatch, navigate]
  );

  const attemptNavigation = useCallback(
    (step: ProgressStepItem) => {
      if (shouldBlockNavigation(step.id)) {
        dispatch(remittanceActions.openDigioNavigationGuard(step));
        return;
      }
      navigateToStep(step);
    },
    [dispatch, navigateToStep, shouldBlockNavigation]
  );

  const onBackArrowClick = useCallback(() => {
    const previousStep = progresStepData[activeStepNumber - 2];
    if (!previousStep) {
      return;
    }
    attemptNavigation(previousStep);
  }, [activeStepNumber, attemptNavigation, progresStepData]);

  const handleStepClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    isEnabled: boolean,
    value: ProgressStepItem
  ) => {
    if (!isEnabled) {
      e.preventDefault();
      return;
    }
    attemptNavigation(value);
  };

  const cancelTransaction = (type: string) => {
    if (type == "yes") {
      navigate("/home");
      dispatch(remittanceActions.resetRemittanceState());
    } else {
      dispatch(remittanceActions.setTransactionCancelModal(false));
    }
  };

  const handleDigioNavigationConfirm = () => {
    const pendingStep = digioNavigationGuard.pendingStep;
    dispatch(remittanceActions.clearDigioEsignData());
    dispatch(remittanceActions.closeDigioNavigationGuard());
    if (pendingStep) {
      navigateToStep(pendingStep);
    }
  };

  const getStepLineColor = (id: number) =>
    id < activeStepNumber ? "bg-primary-1050" : "bg-primary-1100";
  const shouldEnableStep = (id: number, activeStepNumber: number) => id <= activeStepNumber;
  const renderProgresStep = useMemo(() => {
    return progresStepData.map((value, index) => {
      const isEnabled = shouldEnableStep(value.id, activeStepNumber);
      return (
        <li key={index}>
          <div className="relative">
            {index !== progresStepData.length - 1 && (
              <span
                className={clsx(getStepLineColor(value.id), styles.progresStep.stepLine)}
                aria-hidden="true"
              />
            )}
            <div className={styles.progresStep.stepNumberContainer}>
              <div
                className={clsx(
                  value.id <= activeStepNumber ? "bg-primary-1050" : "bg-primary-1100",
                  styles.progresStep.stepRoundNumber
                )}
              >
                <Typography
                  tagType="div"
                  text={value.id}
                  className={clsx(
                    isEnabled ? "text-white" : "text-primary-1050",
                    "font-medium text-sm"
                  )}
                />
              </div>
              <div
                onClick={(e) => {
                  if (activeStepNumber == 6 && value?.id !== 1) {
                    return;
                  }
                  handleStepClick(e, isEnabled, value);
                }}
              >
                <div
                  className={clsx(
                    isEnabled && (activeStepNumber !== 6 || value?.id == 1)
                      ? "cursor-pointer"
                      : "cursor-not-allowed",
                    styles.progresStep.stepTextContainer
                  )}
                >
                  {value.stepName}
                </div>
              </div>
            </div>
          </div>
        </li>
      );
    });
  }, [progresStepData, activeStepNumber, location?.pathname]);

  return (
    <div className={styles.container.outerContainer}>
      <div className={styles.container.innerContainer}>
        <HeaderWithLogo />
      </div>
      <div className="flex items-center my-6 max-w-[1240px] w-full">
        <button
          onClick={onBackArrowClick}
          className={
            activeStepNumber === 1 || activeStepNumber === 6
              ? styles.backArrow.backArrowDisable
              : styles.backArrow.backArrowEnable
          }
          disabled={activeStepNumber === 1 || activeStepNumber === 6}
        >
          <BackArrowBlack />
        </button>

        <ul role="list" className={styles.progresStep.mainContainer}>
          {renderProgresStep}
        </ul>

        <button
          onClick={() => dispatch(remittanceActions.setTransactionCancelModal(true))}
          className={styles.closeIcon}
        >
          <RoundedCloseRed />
        </button>
      </div>
      <ConfirmCancelModal
        visible={transactionCancelModal}
        title={AppStrings.CANCELTRANSACTION.TITLE}
        description={AppStrings.CANCELTRANSACTION.DESCRIPTION}
        onConfirm={() => cancelTransaction("yes")}
        onClose={() => cancelTransaction("no")}
      />
      <ConfirmCancelModal
        visible={digioNavigationGuard.open}
        title={AppStrings.DIGIO_NAVIGATION_GUARD.TITLE}
        description={AppStrings.DIGIO_NAVIGATION_GUARD.DESCRIPTION}
        onConfirm={handleDigioNavigationConfirm}
        onClose={() => dispatch(remittanceActions.closeDigioNavigationGuard())}
      />
    </div>
  );
};

const HeaderWithLogo = React.memo(() => {
  return (
    <div className={styles.header.outer}>
      <div className={styles.header.inner}>
        <div className={styles.header.logo}>
          <MoneyhopLogo />
        </div>
      </div>
    </div>
  );
});
